/**
 * Tab "Histórico" do Guard — lista todos bans (ativos + expirados) ordenados
 * por DataBanimento DESC.
 *
 * - Motivo aparece direto na linha (coluna principal).
 * - Linha clicável → abre /admin/account/{userNum}.
 * - Ações por linha:
 *     - Ver HWID/Detalhes (sempre)
 *     - Desbanir (só se ban ativo)
 */
import { useEffect, useState, useCallback } from 'react';
import {
  AlertCircle, History, RefreshCw, Search, Info, ShieldCheck,
} from 'lucide-react';
import {
  getGuardBansHistory, guardUnban,
  type GuardBanHistoryRow,
} from '@/shared/services/adminService';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useToast } from '@/shared/contexts/ToastContext';
import { DataTable, type DataTableColumn } from '@/shared/components/ui/DataTable';
import { RowActions } from '@/shared/components/ui/RowActions';
import { GuardDetailModal } from './GuardModals';

export default function GuardHistoryPage() {
  const { hasFeature } = useAuth();
  const { notify } = useToast();
  const canBan = hasFeature('ban');

  const [items, setItems] = useState<GuardBanHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [onlyActive, setOnlyActive] = useState(false);

  const [detailUserNum, setDetailUserNum] = useState<number | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await getGuardBansHistory(200, onlyActive));
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Erro');
    } finally {
      setLoading(false);
    }
  }, [onlyActive]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUnban = async (userNum: number) => {
    try {
      await guardUnban(userNum);
      notify('success', 'Desbanido');
      await fetchData();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    }
  };

  const cols: DataTableColumn<GuardBanHistoryRow>[] = [
    {
      key: 'account', label: 'Conta', width: '170px',
      cell: (r) => (
        <div className="min-w-0">
          <div className="text-sm font-bold text-white truncate">
            {r.accountId || `#${r.userNum}`}
          </div>
          <div className="text-[10px] text-white/40 font-mono">#{r.userNum}</div>
        </div>
      ),
    },
    {
      key: 'motivo', label: 'Motivo', width: '1fr',
      cell: (r) => (
        <span className="text-xs text-white/85 line-clamp-2" title={r.motivo}>
          {r.motivo || '—'}
        </span>
      ),
    },
    {
      key: 'status', label: 'Status', width: '90px', align: 'center', mobileHidden: true,
      cell: (r) => r.isActive ? (
        <span className="px-1.5 py-0.5 rounded text-[9px] font-black bg-red-500/20 text-red-400 border border-red-500/30">
          ATIVO
        </span>
      ) : (
        <span className="px-1.5 py-0.5 rounded text-[9px] font-black bg-white/[0.06] text-white/45 border border-white/[0.08]">
          EXPIRADO
        </span>
      ),
    },
    {
      key: 'dataBanimento', label: 'Banido em', width: '160px',
      cell: (r) => (
        <span className="text-[11px] text-white/55 font-mono">
          {new Date(r.dataBanimento).toLocaleString('pt-BR')}
        </span>
      ),
    },
    {
      key: 'dataLiberacao', label: 'Libera em', width: '160px', mobileHidden: true,
      cell: (r) => (
        <span className="text-[11px] text-white/45 font-mono">
          {r.dataLiberacao ? new Date(r.dataLiberacao).toLocaleString('pt-BR') : 'permanente'}
        </span>
      ),
    },
    {
      key: 'actions', label: '', width: '60px', align: 'right', mobileHidden: true,
      cell: (r) => (
        <RowActions
          actions={[
            {
              label: 'Ver HWID / Detalhes',
              icon: <Info size={13} />,
              onClick: () => setDetailUserNum(r.userNum),
              variant: 'primary',
            },
            ...(canBan && r.isActive ? [{
              label: 'Desbanir',
              icon: <ShieldCheck size={13} />,
              onClick: () => handleUnban(r.userNum),
              variant: 'success' as const,
            }] : []),
          ]}
        />
      ),
    },
  ];

  const filtered = filter.trim()
    ? items.filter((r) => {
        const q = filter.toLowerCase();
        return (r.accountId || '').toLowerCase().includes(q)
          || (r.motivo || '').toLowerCase().includes(q)
          || String(r.userNum).includes(q);
      })
    : items;

  const activeCount = items.filter(i => i.isActive).length;

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
            <History className="text-amber-400" size={26} />
            Histórico de Bans
          </h1>
          <p className="text-sm text-white/65 mt-1">
            {items.length.toLocaleString('pt-BR')} bans listados ·
            <span className="text-red-400 font-semibold ml-1">{activeCount.toLocaleString('pt-BR')} ativos</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setOnlyActive(v => !v)}
            className={`px-3 py-2 rounded-lg text-xs font-semibold transition cursor-pointer ${
              onlyActive
                ? 'bg-red-500/15 border border-red-500/30 text-red-400'
                : 'bg-white/[0.04] border border-white/[0.08] text-white/65 hover:bg-white/[0.08]'
            }`}
          >
            {onlyActive ? '🔴 Só ativos' : 'Todos'}
          </button>
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                       bg-amber-500/10 border border-amber-500/30 text-amber-400
                       hover:bg-amber-500/20 transition cursor-pointer disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Atualizar
          </button>
        </div>
      </div>

      <div className="mb-5 relative">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filtrar por conta, UserNum ou motivo..."
          className="w-full bg-[#161e26] border border-white/[0.06] rounded-lg pl-11 pr-4 py-2.5
                     text-sm text-white placeholder:text-white/40
                     focus:outline-none focus:border-amber-500/40 transition"
        />
      </div>

      {error && (
        <div className="surface-card rounded-xl p-4 mb-4 flex items-start gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      <DataTable<GuardBanHistoryRow>
        columns={cols}
        rows={filtered}
        rowKey={(r) => r.banId}
        rowHref={(r) => `/admin/account/${r.userNum}`}
        loading={loading}
        empty={filter ? 'Nenhum resultado pro filtro' : 'Sem histórico'}
        mobileTitle={(r) => r.accountId || `#${r.userNum}`}
        mobileSubtitle={(r) => `${r.isActive ? '🔴 ATIVO' : '⚪ expirado'} · ${r.motivo.slice(0, 80)}`}
        mobileActions={(r) => (
          <RowActions
            actions={[
              {
                label: 'Ver HWID / Detalhes',
                icon: <Info size={13} />,
                onClick: () => setDetailUserNum(r.userNum),
                variant: 'primary',
              },
              ...(canBan && r.isActive ? [{
                label: 'Desbanir',
                icon: <ShieldCheck size={13} />,
                onClick: () => handleUnban(r.userNum),
                variant: 'success' as const,
              }] : []),
            ]}
          />
        )}
      />

      {detailUserNum != null && (
        <GuardDetailModal
          userNum={detailUserNum}
          canBan={canBan}
          onClose={() => setDetailUserNum(null)}
          onBan={() => setDetailUserNum(null)}
          onChanged={fetchData}
        />
      )}
    </div>
  );
}
